import { useCallback, useMemo } from "react";
import { useHistory, useLocation } from "react-router-dom";
import type { MediaTab } from "./types";
import {
	ROOT_PLACEHOLDER,
	URL_FILE_PARAM,
	URL_PATH_PARAM,
	URL_QUERY_PARAM,
	URL_TAB_PARAM,
	URL_TAXONOMY_PARAM,
	isMediaTab
} from "./types";
import { resolvePathValue } from "./utils";

/** État de la page miroité dans l'URL : la seule source de vérité des deep-links. */
export interface StorageUrlState {
	/** Dossier courant, ou ROOT_PLACEHOLDER pour la vue « tous les médias ». */
	path: string;
	/** Fichier sélectionné (panneau de détail), par id. */
	fileId: string | null;
	/** Terme de recherche. */
	q: string;
	/** Onglet de la vue racine (Tous / Images / Documents / Logo). */
	tab: MediaTab;
	/** Filtre taxonomies (ids, séparés par des virgules dans l'URL). */
	taxonomyIds: string[];
}

/** Lit ?path/?file/?q/?tab/?tax depuis une query string : pur, testable sans routeur. */
export const parseStorageSearch = (search: string): StorageUrlState => {
	const params = new URLSearchParams(search);
	const rawTab = (params.get(URL_TAB_PARAM) ?? "").trim().toLowerCase();
	return {
		path: resolvePathValue(params.get(URL_PATH_PARAM)),
		fileId: params.get(URL_FILE_PARAM) || null,
		q: (params.get(URL_QUERY_PARAM) ?? "").trim(),
		tab: isMediaTab(rawTab) ? rawTab : "all",
		taxonomyIds: (params.get(URL_TAXONOMY_PARAM) ?? "")
			.split(",")
			.map((id) => id.trim())
			.filter(Boolean)
	};
};

export interface StorageUrlSync {
	urlState: StorageUrlState;
	/** Ouvre un dossier (ou la racine) : history.PUSH : Précédent/Suivant remontent l'arborescence. */
	navigateToPath: (value: string, options?: { keepSearch?: boolean }) => void;
	/** Change l'onglet de la vue racine : PUSH (c'est une navigation), revient à la racine. */
	setTab: (tab: MediaTab) => void;
	/** Écrit le terme de recherche : REPLACE (pas d'empilement à chaque frappe débouncée). */
	setSearch: (q: string) => void;
	/** Écrit le filtre taxonomies : REPLACE. */
	setTaxonomyIds: (ids: string[]) => void;
	/** Sélectionne un fichier (?file=) : REPLACE : ouvrir/fermer le détail n'empile pas l'historique. */
	selectFile: (fileId: string | null) => void;
	/** Après un upload : ouvre le dossier du fichier ET le sélectionne en UN seul push. */
	showUploaded: (path: string | null | undefined, fileId: string | null | undefined) => void;
}

const buildSearch = (params: URLSearchParams): string => {
	const value = params.toString();
	return value ? `?${value}` : "";
};

/**
 * Centralise la lecture/écriture des paramètres d'URL de la Médiathèque (?path, ?file, ?q, ?tab,
 * ?tax). Les écritures relisent history.location au moment de l'appel (callbacks stables), et
 * l'état exposé est dérivé de useLocation : Précédent/Suivant du navigateur redessinent la page
 * sans effet supplémentaire.
 */
export const useStorageUrlSync = (): StorageUrlSync => {
	const history = useHistory();
	const location = useLocation();

	const urlState = useMemo(() => parseStorageSearch(location.search), [location.search]);

	const apply = useCallback(
		(mode: "push" | "replace", mutate: (params: URLSearchParams) => void) => {
			const current = history.location;
			const params = new URLSearchParams(current.search);
			mutate(params);
			const nextSearch = buildSearch(params);
			const currentSearch = current.search === "?" ? "" : current.search ?? "";
			if (nextSearch === currentSearch) return;
			const target = { pathname: current.pathname, search: nextSearch };
			if (mode === "push") {
				history.push(target);
			} else {
				history.replace(target);
			}
		},
		[history]
	);

	const navigateToPath = useCallback(
		(value: string, options?: { keepSearch?: boolean }) => {
			apply("push", (params) => {
				const normalized = value === ROOT_PLACEHOLDER ? ROOT_PLACEHOLDER : resolvePathValue(value);
				if (normalized === ROOT_PLACEHOLDER) {
					params.delete(URL_PATH_PARAM);
				} else {
					params.set(URL_PATH_PARAM, normalized);
				}
				// Changer de dossier ferme le détail ; l'onglet est un concept de la racine.
				params.delete(URL_FILE_PARAM);
				params.delete(URL_TAB_PARAM);
				if (!options?.keepSearch) {
					params.delete(URL_QUERY_PARAM);
				}
			});
		},
		[apply]
	);

	const setTab = useCallback(
		(tab: MediaTab) => {
			apply("push", (params) => {
				params.delete(URL_PATH_PARAM);
				params.delete(URL_FILE_PARAM);
				if (tab === "all") {
					params.delete(URL_TAB_PARAM);
				} else {
					params.set(URL_TAB_PARAM, tab);
				}
			});
		},
		[apply]
	);

	const setSearch = useCallback(
		(q: string) => {
			apply("replace", (params) => {
				const trimmed = q.trim();
				if (trimmed) {
					params.set(URL_QUERY_PARAM, trimmed);
				} else {
					params.delete(URL_QUERY_PARAM);
				}
			});
		},
		[apply]
	);

	const setTaxonomyIds = useCallback(
		(ids: string[]) => {
			apply("replace", (params) => {
				const cleaned = ids.map((id) => id.trim()).filter(Boolean);
				if (cleaned.length) {
					params.set(URL_TAXONOMY_PARAM, cleaned.join(","));
				} else {
					params.delete(URL_TAXONOMY_PARAM);
				}
			});
		},
		[apply]
	);

	const selectFile = useCallback(
		(fileId: string | null) => {
			apply("replace", (params) => {
				if (fileId) {
					params.set(URL_FILE_PARAM, fileId);
				} else {
					params.delete(URL_FILE_PARAM);
				}
			});
		},
		[apply]
	);

	const showUploaded = useCallback(
		(path: string | null | undefined, fileId: string | null | undefined) => {
			apply("push", (params) => {
				const normalized = path ? resolvePathValue(path) : ROOT_PLACEHOLDER;
				if (normalized === ROOT_PLACEHOLDER) {
					params.delete(URL_PATH_PARAM);
				} else {
					params.set(URL_PATH_PARAM, normalized);
				}
				if (fileId) {
					params.set(URL_FILE_PARAM, fileId);
				} else {
					params.delete(URL_FILE_PARAM);
				}
				params.delete(URL_TAB_PARAM);
				params.delete(URL_QUERY_PARAM);
			});
		},
		[apply]
	);

	return { urlState, navigateToPath, setTab, setSearch, setTaxonomyIds, selectFile, showUploaded };
};
